
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Handshake } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { ShareButtons } from "./posts/ShareButtons";

// Create a custom Clap icon since Lucide doesn't have one
const ClapIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M11 15c.64.8 1.87 1.7 3 1" />
    <path d="M14 13a1 1 0 0 0-1-1h-1a1 1 0 0 1-1-1V9l-3 3v5l1 1" />
    <path d="M9 19c-2.1 0-3-1.9-3-3v-5l3-3v4c0 1 .9 2 2 2h1" />
    <path d="M14 18.5c.6.5 1.5 1 2.5.5" />
    <path d="M7 16.5c-1 .5-1.9.1-2.5-.5" />
    <path d="M16 12c0-1.3 4-1.93 4 3-1 5-2 6-4 7 0-1-1-1-3-2" />
  </svg>
);

interface PostActionsProps {
  onLike: (e?: React.MouseEvent) => void;
  onComment: (e?: React.MouseEvent) => void;
  onSupport: (e?: React.MouseEvent) => void;
  onShare: (e?: React.MouseEvent) => void;
  hasLiked?: boolean;
  hasSupported?: boolean;
  postId?: string;
  content?: string;
  friendsWhoLiked?: string[];
  friendsWhoCommented?: string[];
  friendsWhoSupported?: string[];
}

export const PostActions = ({ 
  onLike, 
  onComment, 
  onSupport,
  onShare,
  hasLiked = false,
  hasSupported = false,
  postId = '',
  content = '',
  friendsWhoLiked = [],
  friendsWhoCommented = [],
  friendsWhoSupported = []
}: PostActionsProps) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleLike = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onLike(e);
    if (!hasLiked) {
      toast({
        title: "Spread the love! ❤️",
        description: "Your positive energy makes a difference!",
      });
    }
  };

  const handleComment = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onComment(e);
  };

  const handleSupport = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onSupport(e);
  };

  const getFriendEngagementText = (friends: string[], action: string) => {
    if (friends.length === 0) return '';
    if (friends.length === 1) return `${friends[0]} ${action}`;
    if (friends.length === 2) return `${friends[0]} and ${friends[1]} ${action}`;
    if (friends.length === 3) return `${friends[0]}, ${friends[1]} and ${friends[2]} ${action}`;
    return `${friends[0]}, ${friends[1]} and ${friends.length - 2} other friends ${action}`;
  };

  // Use smaller icon sizes and more compact layout on mobile
  const iconSize = isMobile ? "w-4 h-4" : "w-5 h-5";
  const textSize = isMobile ? "text-sm" : "text-base";
  
  return (
    <div className="flex items-center w-full gap-2 min-w-0" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-center">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleLike} 
          className={`rounded-full flex items-center gap-2 px-2 md:px-3 hover:bg-pink-100 dark:hover:bg-pink-900/30 ${hasLiked ? 'text-pink-500' : 'text-gray-600 dark:text-gray-300'}`}
        >
          <Heart className={`${iconSize} ${hasLiked ? 'fill-current' : ''}`} />
          {!isMobile && friendsWhoLiked.length > 0 && (
            <span className={`${textSize} font-medium`}>{getFriendEngagementText(friendsWhoLiked, 'liked this')}</span>
          )}
        </Button>
      </div>
      
      <div className="flex items-center justify-center">
        <Button 
          variant="ghost" 
          size="sm" 
          className="rounded-full flex items-center gap-2 px-2 md:px-3 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-600 dark:text-gray-300"
          onClick={handleComment}
        >
          <MessageCircle className={iconSize} />
          {!isMobile && (
            <span className={`${textSize} font-medium`}>
              {friendsWhoCommented.length > 0 ? getFriendEngagementText(friendsWhoCommented, 'commented') : 'Join conversation'}
            </span>
          )}
        </Button>
      </div>

      <div className="flex-1"></div>

      <div className="flex items-center justify-center">
        <Button 
          variant="ghost" 
          size="sm" 
          className={`rounded-full flex items-center gap-2 px-2 md:px-3 hover:bg-purple-100 dark:hover:bg-purple-900/30 ${hasSupported ? 'text-purple-500' : 'text-gray-600 dark:text-gray-300'}`}
          onClick={handleSupport}
        >
          <Handshake className={iconSize} />
          {!isMobile && (
            <span className={`${textSize} font-medium`}>
              {friendsWhoSupported.length > 0 ? getFriendEngagementText(friendsWhoSupported, 'supported this') : 'Support'}
            </span>
          )}
        </Button>
      </div>

      {postId && content && (
        <div className="md:shrink-0">
          <ShareButtons postId={postId} content={content} />
        </div>
      )}
    </div>
  );
};
